import React, { FC, ReactElement } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

export type SummaryStatusTone = 'positive' | 'caution' | 'negative' | 'neutral';

export type SummaryStatusIcon = 'check' | 'warning' | 'down' | 'up' | 'unknown';

export interface SummaryStatusPresentation {
  label: string;
  description: string;
  tone: SummaryStatusTone;
  icon: SummaryStatusIcon;
}

const STATUS_ICONS: Record<SummaryStatusIcon, ReactElement> = {
  check: <CheckCircleOutlineIcon />,
  warning: <ErrorOutlineIcon />,
  down: <ArrowDownwardIcon />,
  up: <ArrowUpwardIcon />,
  unknown: <HelpOutlineIcon />,
};

const TONE_COLORS: Record<
  SummaryStatusTone,
  { foreground: string; background: string }
> = {
  positive: {
    foreground: Colours.OV_ACCENT_GREEN,
    background: Colours.OV_FERTILE,
  },
  caution: {
    foreground: Colours.OV_ACCENT_AMBER,
    background: Colours.OV_HIGH_OVULATION,
  },
  negative: {
    foreground: Colours.OV_ACCENT_MAGENTA,
    background: Colours.OV_PERIOD_FLOW,
  },
  neutral: {
    foreground: Colours.OV_CHARCOAL,
    background: Colours.LIGHT_GRAY_BLUE,
  },
};

interface OvSummaryStatusProps {
  presentation: SummaryStatusPresentation;
  size?: 'default' | 'large';
}

const OvSummaryStatus: FC<OvSummaryStatusProps> = ({
  presentation,
  size = 'default',
}) => {
  const colors = TONE_COLORS[presentation.tone];

  return (
    <StatusContainer>
      <Badge
        $foreground={colors.foreground}
        $background={colors.background}
        $large={size === 'large'}
      >
        <Icon $large={size === 'large'} aria-hidden="true">
          {STATUS_ICONS[presentation.icon]}
        </Icon>
        {presentation.label}
      </Badge>
    </StatusContainer>
  );
};

export default OvSummaryStatus;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const Badge = styled.span<{
  $foreground: string;
  $background: string;
  $large: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $large }) => ($large ? '0.5rem' : '0.375rem')};
  max-width: 100%;
  padding: ${({ $large }) => ($large ? '0.5rem 0.875rem' : '0.375rem 0.75rem')};
  border-radius: ${Variables.borderRadius.XLARGE};
  color: ${({ $foreground }) => $foreground};
  background: ${({ $background }) => $background};
  font-size: ${({ $large }) =>
    $large ? Variables.fontSizes.LARGE : Variables.fontSizes.MEDIUM};
  font-weight: bold;
  line-height: ${Variables.lineHeights.LARGE};
  white-space: nowrap;
`;

const Icon = styled.span<{ $large: boolean }>`
  display: inline-flex;
  flex-shrink: 0;

  .MuiSvgIcon-root {
    width: ${({ $large }) => ($large ? '1.125rem' : '1rem')};
    height: ${({ $large }) => ($large ? '1.125rem' : '1rem')};
  }
`;
